import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import AllocationToDelivery from "./AllocationToDelivery/allocationToDelivery";
import OrderToAllocation from "./OrderToAllocation/orderToAllocation";
import { fetchOrderToAllocationFilters } from "../../../api/LossRecoveryApi";

const OrderExecuationMonitor = ({ sideViewTab }) => {
  const { accounts } = useMsal();
  const activeAccount = accounts?.[0];

  const [sharedSelectedFilters, setSharedSelectedFilters] = useState({});
  const [sharedUOM, setSharedUOM] = useState("Eaches");
  const [sharedFiltersData, setSharedFiltersData] = useState([]);
  const [sharedColumnsData, setSharedColumnsData] = useState([]);
  const [sharedFilterLoading, setSharedFilterLoading] = useState(false);
  const [hasFetchedSharedFilters, setHasFetchedSharedFilters] = useState(false);

  const isFetchingFiltersRef = useRef(false);
  const filterAbortControllerRef = useRef(new AbortController());

  const mergeSelectionsIntoFilters = useCallback((apiFilters = [], selectedMap = {}) => {
    return apiFilters.map((filter) => ({
      ...filter,
      selectedData: selectedMap[filter.label] || filter.selectedData || [],
    }));
  }, []);

  const fetchSharedFilters = useCallback(
    async ({
      selectedMap = {},
      uom = "Eaches",
      force = false,
    } = {}) => {
      if (!activeAccount?.username) return;

      // Prevent refetch on tab switch (only if not forced)
      if (hasFetchedSharedFilters && !force) return;
      
      // If currently fetching, abort the previous request first
      if (isFetchingFiltersRef.current) {
        filterAbortControllerRef.current?.abort();
      }

      setSharedFilterLoading(true);
      isFetchingFiltersRef.current = true;

      // Create new abort controller for this request
      filterAbortControllerRef.current = new AbortController();

      try {
        const payload = {
          email: activeAccount.username,
          uom,
          filters: selectedMap || {},
          type: "Order To Execution View",
        };

        const response = await fetchOrderToAllocationFilters(
          payload,
          filterAbortControllerRef.current.signal
        );

        const mergedFilters = mergeSelectionsIntoFilters(
          response?.filters || [],
          selectedMap
        );

        setSharedFiltersData(mergedFilters);
        setSharedColumnsData(response?.columns || []);
        setHasFetchedSharedFilters(true);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.error("Error fetching shared filters:", error);
        }
      } finally {
        setSharedFilterLoading(false);
        isFetchingFiltersRef.current = false;
      }
    },
    [activeAccount?.username, hasFetchedSharedFilters, mergeSelectionsIntoFilters]
  );

  return (
    <div>
      {sideViewTab === "Order To Allocation" ? (
        <OrderToAllocation
          sharedSelectedFilters={sharedSelectedFilters}
          setSharedSelectedFilters={setSharedSelectedFilters}
          sharedUOM={sharedUOM}
          setSharedUOM={setSharedUOM}
          sharedFiltersData={sharedFiltersData}
          setSharedFiltersData={setSharedFiltersData}
          sharedColumnsData={sharedColumnsData}
          sharedFilterLoading={sharedFilterLoading}
          hasFetchedSharedFilters={hasFetchedSharedFilters}
          setHasFetchedSharedFilters={setHasFetchedSharedFilters}
          fetchSharedFilters={fetchSharedFilters}
        />
      ) : (
        <AllocationToDelivery
          sharedSelectedFilters={sharedSelectedFilters}
          setSharedSelectedFilters={setSharedSelectedFilters}
          sharedUOM={sharedUOM}
          setSharedUOM={setSharedUOM}
          sharedFiltersData={sharedFiltersData}
          setSharedFiltersData={setSharedFiltersData}
          sharedColumnsData={sharedColumnsData}
          sharedFilterLoading={sharedFilterLoading}
          hasFetchedSharedFilters={hasFetchedSharedFilters}
          setHasFetchedSharedFilters={setHasFetchedSharedFilters}
          fetchSharedFilters={fetchSharedFilters}
        />
        // <>In progress...</>
      )}
    </div>
  );
};

OrderExecuationMonitor.propTypes = {
  sideViewTab: PropTypes.string,
};

export default OrderExecuationMonitor;